import React from "react";
import {
  Box,
  Container,
  Text,
  Button,
  Grid,
  GridItem,
  VStack,
  HStack,
  Input,
} from "@chakra-ui/react";
import { motion, AnimatePresence } from "framer-motion";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { contactService } from "../firebase/contactService.js";
import { useNavigate } from "react-router-dom";
const mediaConfig = {
  documentApproved: {
    image: "https://picsum.photos/640/420?random=220",
    alt: "Document approuvé avec tampon validé",
  },
};

const Hero = () => {
  const MotionBox = motion.div;

  const MiniWizard = () => {
    const navigate = useNavigate();
    const [step, setStep] = React.useState(0);
    const [values, setValues] = React.useState({
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
      service: "",
    });
    const [isAnimating, setIsAnimating] = React.useState(false);
    const [phoneNumber, setPhoneNumber] = React.useState("");
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [notification, setNotification] = React.useState(null);

    const fields = [
      { key: "firstName", label: "Prénom", placeholder: "Votre prénom" },
      { key: "lastName", label: "Nom", placeholder: "Votre nom" },
      {
        key: "phone",
        label: "Téléphone",
        placeholder: "Votre numéro de téléphone",
      },
      { key: "email", label: "Email", placeholder: "votre@email.com" },
      {
        key: "service",
        label: "Service",
        placeholder: "Sélectionnez un service",
      },
    ];

    const serviceOptions = [
      "Première demande de titre de séjour",
      "Renouvellement de titre de séjour",
      "Demande de visa",
      "OQTF",
      "Contestation d'une décision administrative",
    ];
    const current = fields[step];

    const onChange = (e) => {
      const value = e.target.value;
      setValues({ ...values, [current.key]: value });
    };

    const onPhoneChange = (value, country) => {
      setPhoneNumber(value);
      setValues({ ...values, phone: value });
    };

    const next = () => {
      if (current.key === "phone") {
        if (phoneNumber.trim() === "") return;
      } else {
        if (values[current.key].trim() === "") return;
      }
      setIsAnimating(true);
      setTimeout(() => {
        setStep((s) => Math.min(s + 1, fields.length - 1));
        setIsAnimating(false);
      }, 300);
    };

    const prev = () => {
      setIsAnimating(true);
      setTimeout(() => {
        setStep((s) => Math.max(s - 1, 0));
        setIsAnimating(false);
      }, 300);
    };

    const onSubmit = async (e) => {
      e.preventDefault();

      // Validation finale
      if (current.key === "phone") {
        if (phoneNumber.trim() === "") return;
      } else {
        if (values[current.key].trim() === "") return;
      }

      // Si c'est la dernière étape, envoyer les données
      if (step === fields.length - 1) {
        setIsSubmitting(true);

        try {
          const contactData = {
            firstName: values.firstName,
            lastName: values.lastName,
            phone: values.phone,
            email: values.email,
            service: values.service || "Non spécifié",
            source: "hero-form",
          };

          // Validation
          const validation = contactService.validateContact(contactData);
          if (!validation.isValid) {
            setNotification({
              status: "error",
              title: "Erreur de validation",
              description: validation.errors.join(", "),
            });
            return;
          }

          // Envoyer à Firestore
          const result = await contactService.addContact(contactData);

          // Succès - rediriger vers la page de succès
          navigate('/success');
        } catch (error) {
          console.error("Erreur:", error);
          setNotification({
            status: "error",
            title: "Erreur",
            description: "Une erreur est survenue. Veuillez réessayer.",
          });

          // Masquer la notification après 5 secondes
          setTimeout(() => setNotification(null), 5000);
        } finally {
          setIsSubmitting(false);
        }
      } else {
        // Continuer vers l'étape suivante
        next();
      }
    };

    return (
      <Box as="form" onSubmit={onSubmit} position="relative" overflow="hidden">
        {/* Notifications */}
        {notification && (
          <Box
            bg={
              notification.status === "success"
                ? "rgba(34, 197, 94, 0.95)"
                : "rgba(239, 68, 68, 0.95)"
            }
            color="white"
            borderRadius="2xl"
            p={4}
            mb={6}
            backdropFilter="blur(10px)"
            border="1px solid"
            borderColor="whiteAlpha.300"
          >
            <VStack spacing={2} align="center" w="100%">
              <Text fontSize="2xl">
                {notification.status === "success" ? "✅" : "❌"}
              </Text>
              <Text fontSize="md" fontWeight={700}>
                {notification.title}
              </Text>
              <Text fontSize="sm">{notification.description}</Text>
            </VStack>
          </Box>
        )}

        {/* Indicateur d'étapes */}
        <HStack mb={6} justify="center" spacing={3} py={2} overflow="visible">
          {fields.map((_, index) => (
            <Box
              key={index}
              w="14px"
              h="14px"
              borderRadius="full"
              bg={index <= step ? "#0053b3" : "#e5e7eb"}
              transition="all 0.4s ease-in-out"
              boxShadow={index === step ? "0 0 0 3px rgba(0, 83, 179, 0.2)" : "none"}
            />
          ))}
        </HStack>

        {/* Titre simple */}
        <Text
          fontSize="lg"
          color="#1f2937"
          textAlign="center"
          mb={6}
          fontWeight={700}
        >
          {step === 0 && "Comment vous appelez-vous ?"}
          {step === 1 && "Votre nom de famille ?"}
          {step === 2 && "Votre numéro de téléphone ?"}
          {step === 3 && "Votre adresse email ?"}
          {step === 4 && "Quel service vous intéresse ?"}
        </Text>

        <AnimatePresence mode="wait">
          <MotionBox
            key={current.key}
            initial={{ opacity: 0, x: isAnimating ? 50 : -50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: isAnimating ? -50 : 50 }}
            transition={{
              duration: 0.3,
              ease: "easeInOut",
            }}
          >
            <VStack spacing={4} align="start">
              {/* Input */}
              {current.key === "phone" ? (
                <Box
                  position="relative"
                  zIndex={10}
                  w="full"
                  sx={{
                    ".react-tel-input": {
                      width: "100% !important",
                      ".form-control": {
                        width: "100% !important",
                        height: "56px !important",
                        borderRadius: "16px !important",
                        border: "2px solid #d1d5db !important",
                        backgroundColor: "white !important",
                        fontSize: "16px !important",
                        fontWeight: "500 !important",
                        color: "#1f2937 !important",
                        paddingLeft: "60px !important",
                        paddingRight: "20px !important",
                        textAlign: "left !important",
                        "&:focus": {
                          borderColor: "#0053b3 !important",
                          boxShadow:
                            "0 0 0 3px rgba(0, 83, 179, 0.1) !important",
                          backgroundColor: "white !important",
                        },
                        "&:hover": {
                          borderColor: "#0053b3 !important",
                          backgroundColor: "white !important",
                        },
                        "&::placeholder": {
                          color: "#6b7280 !important",
                          fontWeight: "400 !important",
                        },
                      },
                      ".flag-dropdown": {
                        border: "2px solid #d1d5db !important",
                        borderRight: "none !important",
                        borderRadius: "16px 0 0 16px !important",
                        backgroundColor: "white !important",
                        "&.open": {
                          borderColor: "#0053b3 !important",
                          backgroundColor: "white !important",
                        },
                        "&:hover": {
                          borderColor: "#0053b3 !important",
                          backgroundColor: "white !important",
                        },
                      },
                      ".selected-flag": {
                        borderRadius: "14px 0 0 14px !important",
                        backgroundColor: "transparent !important",
                        padding: "0 8px !important",
                        "&:hover": {
                          backgroundColor: "transparent !important",
                        },
                        "&:focus": {
                          backgroundColor: "transparent !important",
                        },
                      },
                      ".country-list": {
                        borderRadius: "16px !important",
                        border: "2px solid #e5e7eb !important",
                        boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1) !important",
                        maxHeight: "200px !important",
                        overflowY: "auto !important",
                        zIndex: "9999 !important",
                      },
                    },
                  }}
                >
                  <PhoneInput
                    country="fr"
                    value={phoneNumber}
                    onChange={onPhoneChange}
                    placeholder="Votre numéro de téléphone"
                    enableSearch={true}
                    searchPlaceholder="Rechercher un pays..."
                    autoFocus
                    preferredCountries={[
                      "fr",
                      "us",
                      "de",
                      "gb",
                      "es",
                      "it",
                      "be",
                      "ch",
                      "ca",
                      "ma",
                      "tn",
                      "dz",
                    ]}
                    excludeCountries={[]}
                    containerStyle={{
                      width: "100%",
                    }}
                    inputStyle={{
                      width: "100%",
                      height: "56px",
                      borderRadius: "16px",
                      border: "2px solid #d1d5db",
                      backgroundColor: "white",
                      fontSize: "16px",
                      fontWeight: "500",
                      color: "#1f2937",
                      paddingLeft: "60px",
                      paddingRight: "20px",
                    }}
                    buttonStyle={{
                      border: "2px solid #d1d5db",
                      borderRight: "none",
                      borderRadius: "16px 0 0 16px",
                      backgroundColor: "white",
                    }}
                    dropdownStyle={{
                      borderRadius: "16px",
                      border: "2px solid #e5e7eb",
                      boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
                      maxHeight: "200px",
                    }}
                  />
                </Box>
              ) : current.key === "service" ? (
                <Box
                  as="select"
                  value={values[current.key]}
                  onChange={onChange}
                  size="lg"
                  borderRadius="2xl"
                  bg="white"
                  border="2px solid"
                  borderColor="#d1d5db"
                  color="#1f2937"
                  fontSize="lg"
                  fontWeight={500}
                  w="100%"
                  h="56px"
                  px={4}
                  _focus={{
                    borderColor: "#0053b3",
                    boxShadow: "0 0 0 3px rgba(0, 83, 179, 0.1)",
                    bg: "white",
                    color: "#1f2937",
                    outline: "none",
                  }}
                  _hover={{
                    borderColor: "#0053b3",
                    bg: "white",
                  }}
                  autoFocus
                >
                  <option value="">Sélectionnez un service</option>
                  {serviceOptions.map((option, index) => (
                    <option key={index} value={option}>
                      {option}
                    </option>
                  ))}
                </Box>
              ) : (
                <Input
                  type={current.key === "email" ? "email" : "text"}
                  value={values[current.key]}
                  onChange={onChange}
                  placeholder={current.placeholder}
                  size="lg"
                  borderRadius="2xl"
                  bg="white"
                  border="2px solid"
                  borderColor="#d1d5db"
                  color="#1f2937"
                  fontSize="lg"
                  fontWeight={500}
                  _placeholder={{ color: "#6b7280", fontWeight: 400 }}
                  _focus={{
                    borderColor: "#0053b3",
                    boxShadow: "0 0 0 3px rgba(0, 83, 179, 0.1)",
                    bg: "white",
                    color: "#1f2937",
                  }}
                  _hover={{
                    borderColor: "#0053b3",
                    bg: "white",
                  }}
                  autoFocus
                />
              )}

              {/* Boutons */}
              <HStack spacing={3} w="full" mt={4}>
                {step > 0 && (
                  <Button
                    variant="outline"
                    flex="1"
                    borderColor="#0053b3"
                    color="#0053b3"
                    _hover={{ bg: "#0053b3", color: "white" }}
                    onClick={prev}
                    borderRadius="xl"
                    size="lg"
                    fontWeight={600}
                  >
                    ← Retour
                  </Button>
                )}
                <Button
                  type="submit"
                  flex="1"
                  bg="#0053b3"
                  color="white"
                  _hover={{ bg: "#004494" }}
                  fontWeight={700}
                  borderRadius="xl"
                  size="lg"
                  isLoading={isSubmitting}
                  loadingText="Envoi..."
                  isDisabled={
                    current.key === "phone"
                      ? phoneNumber.trim() === ""
                      : values[current.key].trim() === ""
                  }
                  opacity={
                    current.key === "phone"
                      ? phoneNumber.trim() === ""
                        ? 0.5
                        : 1
                      : values[current.key].trim() === ""
                      ? 0.5
                      : 1
                  }
                  _disabled={{ bg: "#9ca3af", color: "white" }}
                >
                  {step < fields.length - 1 ? "Continuer →" : "Envoyer"}
                </Button>
              </HStack>
            </VStack>
          </MotionBox>
        </AnimatePresence>

        {/* Progression */}
        <Text
          fontSize="sm"
          color="#374151"
          textAlign="center"
          mt={6}
          fontWeight={500}
        >
          {step + 1} sur {fields.length}
        </Text>
      </Box>
    );
  };

  const FormCard = () => (
    <Box
      borderRadius="3xl"
      overflow="hidden"
      boxShadow="0 25px 80px rgba(0, 83, 179, 0.15)"
      bg="rgba(255, 255, 255, 0.82)"
      border="1px solid"
      borderColor="whiteAlpha.500"
      backdropFilter="blur(8px)"
      p={{ base: 6, lg: 8 }}
      position="relative"
    >
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="linear-gradient(135deg, rgba(0, 83, 179, 0.02) 0%, rgba(239, 51, 64, 0.02) 100%)"
        pointerEvents="none"
      />
      <VStack align="stretch" spacing={6} position="relative" zIndex={1}>
        <VStack spacing={2}>
          <Text fontWeight={800} fontSize="xl" color="#0053b3">
            Commencez maintenant
          </Text>
          <Text
            fontSize="sm"
            color="#374151"
            textAlign="center"
            fontWeight={500}
          >
            Remplissez vos informations en 4 étapes simples
          </Text>
        </VStack>
        <MiniWizard />
      </VStack>
    </Box>
  );

  return (
    <Box as="section" bg="transparent" mt={10} mb={{ base: 0, lg: 10 }}>
      <Container maxW="1200px" px={5} py={{ base: 12, lg: 20 }}>
        <Grid
          templateColumns={{ base: "1fr", lg: "1.1fr 0.9fr" }}
          gap={{ base: 10, lg: 16 }}
          alignItems="center"
        >
          <GridItem>
            <VStack align="start" spacing={6}>
              <Box
                w="64px"
                h="6px"
                bg="linear-gradient(90deg, #0053b3 0%, #ffffff 50%, #ef3340 100%)"
                borderRadius="full"
              />
              <Text
                as="h1"
                fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
                fontWeight={800}
                lineHeight="1.15"
                color="white"
                mb={{ base: 3, lg: 4 }}
              >
                Déposez votre demande en quelques clics
              </Text>
              {/* Liste besoins (simple, pour publics non francophones) */}
              <VStack
                align="start"
                spacing={2}
                color="white"
                fontWeight="bold"
                mt={{ base: 4, lg: 6 }}
                textAlign="left"
              >
                <Text fontWeight={700} fontSize={{ base: 'lg', lg: 'xl' }}>Nous aidons pour :</Text>
                <Box pl={3} borderLeft="4px solid" borderColor="#0053b3">
                  <VStack align="start" spacing={1}>
                    <Text fontSize={{ base: 'md', lg: 'lg' }}>• Première demande de titre de séjour</Text>
                    <Text fontSize={{ base: 'md', lg: 'lg' }}>• Renouvellement de titre de séjour</Text>
                    <Text fontSize={{ base: 'md', lg: 'lg' }}>• Demande de visa</Text>
                    <Text fontSize={{ base: 'md', lg: 'lg' }}>• OQTF (recours)</Text>
                    <Text fontSize={{ base: 'md', lg: 'lg' }}>• Contestation d'une décision administrative</Text>
                  </VStack>
                </Box>
              </VStack>

              {/* Liste d'avantages supprimée (désormais dans la banderolle) */}
            </VStack>
          </GridItem>
          <GridItem display={{ base: "none", lg: "block" }}>
            <FormCard />
          </GridItem>
        </Grid>
      </Container>

      {/* Banderolle des avantages (entre le Hero et les services) */}
      <Box px={5} mt={{ base: 6, lg: 10 }} display={{ base: 'none', md: 'block' }}>
        <Box
          maxW="1200px"
          mx="auto"
          bg="rgba(255, 255, 255, 0.85)"
          border="1px solid"
          borderColor="whiteAlpha.600"
          boxShadow="0 10px 30px rgba(0,0,0,0.08)"
          backdropFilter="blur(6px)"
          borderRadius="2xl"
          px={{ base: 4, md: 8 }}
          py={{ base: 3, md: 4 }}
        >
          <HStack
            spacing={{ base: 6, md: 12, lg: 16 }}
            justify={{ base: "center", md: "space-between" }}
            align="center"
            flexWrap={{ base: "wrap", md: "nowrap" }}
            w="100%"
          >
            <Text fontWeight={800} color="#0053b3" fontSize={{ base: "sm", md: "md" }}>Rappel en 24h ouvrées</Text>
            <Text display={{ base: "none", md: "block" }} color="black" fontSize="2xl">•</Text>
            <Text fontWeight={800} color="#0053b3" fontSize={{ base: "sm", md: "md" }}>Dépôt sous 48h (dossier complet)</Text>
            <Text display={{ base: "none", md: "block" }} color="black" fontSize="2xl">•</Text>
            <Text fontWeight={800} color="#0053b3" fontSize={{ base: "sm", md: "md" }}>Tarif clair forfaitaire</Text>
            <Text display={{ base: "none", md: "block" }} color="black" fontSize="2xl">•</Text>
            <Text fontWeight={800} color="#0053b3" fontSize={{ base: "sm", md: "md" }}>Attestation de dépôt accessible 2 ans</Text>
          </HStack>
        </Box>
      </Box>
      {/* Formulaire placé plus bas pour mobile */}
      <Box
        id="mobile-form"
        display={{ base: "block", lg: "none" }}
        px={0}
        mt={4}
      >
        <Box
          position="relative"
          left="50%"
          ml="-50vw"
          w="100vw"
          bg="linear-gradient(90deg, #0053b3, #ef3340)"
          py={8}
          mb={{ base: -1, lg: 0 }}
        >
          <Container maxW="1200px" px={5}>
            <FormCard />
          </Container>
        </Box>
      </Box>
    </Box>
  );
};

export default Hero;
